import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Cctv, Radio, Loader2, PlaySquare, AlertCircle, X } from 'lucide-react';

const LiveCameras = () => {
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCamera, setSelectedCamera] = useState(null);

    const formatBytes = (bytes) => {
        const val = parseInt(bytes || 0, 10);
        if (val === 0) return '0 B';
        if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
        if (val < 1024 * 1024 * 1024) return `${(val / 1024 / 1024).toFixed(1)} MB`;
        return `${(val / 1024 / 1024 / 1024).toFixed(2)} GB`;
    };

    useEffect(() => {
        fetchCameras();
    }, []);

    const fetchCameras = async () => {
        try {
            setLoading(true);
            const projectId = localStorage.getItem('activeProjectId');
            const token = localStorage.getItem('token');
            const res = await axios.get('http://localhost:4000/api/cameras', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });
            setCameras(res.data);
        } catch (err) {
            console.error("Error fetching cameras:", err);
            setError("No se pudieron cargar las cámaras. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-theme-bg flex items-center justify-center text-theme-text-primary">
                <Loader2 size={48} className="animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans selection:bg-blue-500/30 animate-fade-in">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2">
                        Red de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Cámaras en Vivo</span>
                    </h1>
                    <p className="text-theme-text-secondary font-medium">Visualiza el flujo de streaming en tiempo real de tu obra.</p>
                </div>
            </div>

            {error && (
                <div className="max-w-7xl mx-auto bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 flex items-center gap-3">
                    <AlertCircle size={20} /> {error}
                </div>
            )}

            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-20">
                {cameras.length === 0 && !error ? (
                    <div className="col-span-full bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-16 text-center shadow-2xl">
                        <Cctv size={64} className="mx-auto text-slate-700 mb-6 drop-shadow-lg" />
                        <h3 className="text-2xl font-bold text-theme-text-primary tracking-tight">Sin cámaras conectadas</h3>
                        <p className="text-theme-text-secondary mt-3 font-medium text-lg">No se han detectado flujos de red vinculados a este proyecto.</p>
                    </div>
                ) : (
                    cameras.map((cam) => (
                        <div key={cam.id} className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl overflow-hidden shadow-2xl group hover:border-blue-500/30 transition-all duration-300 hover:shadow-blue-900/10">
                            {/* Video Container Placeholder */}
                            <div 
                                onClick={() => setSelectedCamera(cam)}
                                className="relative aspect-video bg-theme-bg flex items-center justify-center overflow-hidden border-b border-theme-border cursor-pointer group/vid"
                            >
                                <VideoPlaceholder url={cam.rtsp_url} name={cam.name} lastImageUrl={cam.last_image_url} />
                                <div className="absolute inset-0 bg-black/0 group-hover/vid:bg-black/20 transition-all flex items-center justify-center z-10">
                                    <span className="opacity-0 group-hover/vid:opacity-100 bg-blue-600/90 text-white text-xs font-bold px-3 py-1.5 rounded-full transition-all shadow-lg scale-90 group-hover/vid:scale-100">
                                        Ampliar Vista
                                    </span>
                                </div>
                                <div className="absolute top-4 left-4 bg-theme-bg/60 backdrop-blur-md px-3 py-1.5 rounded-lg border border-theme-border flex items-center gap-2 shadow-lg z-20">
                                    <Radio size={14} className="text-red-500 animate-pulse" />
                                    <span className="text-xs font-black text-theme-text-primary uppercase tracking-widest">EN VIVO</span>
                                </div>
                            </div>

                            {/* Camera Info */}
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-theme-text-primary mb-2 tracking-tight">{cam.name}</h3>
                                <p className="text-sm text-theme-text-secondary font-medium mb-5 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]"></span>
                                    Zona de Cobertura: {cam.location_description || 'No especificada'}
                                </p>
                                <div className="bg-theme-bg border border-theme-border rounded-xl p-3 text-[11px] font-mono text-theme-text-secondary truncate hover:text-slate-300 transition-colors cursor-pointer" title={cam.rtsp_url}>
                                    {cam.rtsp_url}
                                </div>
                                <div className="mt-4 flex justify-between items-center text-xs text-theme-text-secondary border-t border-theme-border/30 pt-3">
                                    <span>Datos consumidos:</span>
                                    <span className="font-bold text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-lg border border-blue-500/20">
                                        {formatBytes(cam.data_consumed_bytes)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal de Cámara Ampliada */}
            {selectedCamera && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fade-in">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-6xl w-full overflow-hidden shadow-2xl">
                        <div className="p-6 border-b border-theme-border flex justify-between items-center bg-theme-bg/50">
                            <div>
                                <h3 className="text-2xl font-bold text-theme-text-primary tracking-tight">{selectedCamera.name}</h3>
                                <p className="text-sm text-theme-text-secondary mt-1">Zona de Cobertura: {selectedCamera.location_description || 'No especificada'}</p>
                            </div>
                            <button 
                                onClick={() => setSelectedCamera(null)}
                                className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-border/20 rounded-xl transition-all"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
                            <WebRTCPlayer cam={selectedCamera} />
                            <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-lg border border-theme-border flex items-center gap-2 shadow-lg z-20">
                                <Radio size={14} className="text-red-500 animate-pulse" />
                                <span className="text-xs font-black text-theme-text-primary uppercase tracking-widest">EN VIVO (Ampliado)</span>
                            </div>
                        </div>
                        <div className="p-6 bg-theme-bg/50 flex justify-between items-center text-xs font-mono text-theme-text-secondary">
                            <span className="truncate max-w-md">{selectedCamera.rtsp_url}</span>
                            <span className="bg-theme-border/40 px-3 py-1.5 rounded-lg border border-theme-border">ID: {selectedCamera.id}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Componente para manejar el visor
const VideoPlaceholder = ({ url, name, lastImageUrl }) => {
    // Si la cámara entrega un stream por HTTP nativo o app de celular
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        return (
            <div className="absolute inset-0 z-10">
                <img
                    src={url}
                    alt={name}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                />
                {/* Fallback si la imagen no carga */}
                <div style={{ display: 'none' }} className="absolute inset-0 flex-col items-center justify-center text-slate-600 bg-gradient-to-b from-theme-bg to-theme-bg">
                    <AlertCircle size={36} className="mb-3 opacity-50 relative z-10 text-rose-500" />
                    <span className="text-xs font-bold tracking-widest uppercase opacity-75 relative z-10">Error de Conexión IP</span>
                </div>
            </div>
        );
    }

    // Si tenemos la última captura de la cámara extraída automáticamente por el worker
    if (lastImageUrl) {
        return (
            <div className="absolute inset-0 z-10">
                <img
                    src={lastImageUrl}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                />
            </div>
        );
    }

    // Placeholder por defecto para flujos RTSP puros que no pueden ser renderizados nativamente por el navegador HTML5
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600 bg-gradient-to-b from-theme-bg to-theme-bg z-10">
            <PlaySquare size={36} className="mb-3 opacity-50 relative z-10 text-blue-500" />
            <span className="text-xs font-bold tracking-widest uppercase opacity-80 relative z-10 text-blue-100">Flujo Protegido RTSP</span>
            <span className="text-[10px] text-theme-text-secondary mt-2 font-medium px-8 text-center">(Haz clic para abrir WebRTC)</span>
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/tv-noise.png')] mix-blend-overlay"></div>
        </div>
    );
};

const WebRTCPlayer = ({ cam }) => {
    const videoRef = React.useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [streamType, setStreamType] = useState('sub');
    const [secondsWatched, setSecondsWatched] = useState(0);

    useEffect(() => {
        if (isPaused) return;

        setLoading(true);
        setError(null);

        let pc = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        pc.addTransceiver('video', { direction: 'recvonly' });
        pc.addTransceiver('audio', { direction: 'recvonly' });
        
        pc.ontrack = (event) => {
            if (videoRef.current && event.streams && event.streams[0]) {
                videoRef.current.srcObject = event.streams[0];
            }
        };
        
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') {
                setLoading(false);
            } else if (pc.connectionState === 'failed') {
                setError('Falló la conexión de video.');
                setLoading(false);
            }
        };

        const startWebRTC = async () => {
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                const token = localStorage.getItem('token');
                const projectId = localStorage.getItem('activeProjectId');
                const response = await fetch(`http://localhost:4000/api/cameras/${cam.id}/webrtc`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'x-project-id': projectId
                    },
                    body: JSON.stringify({ sdp: pc.localDescription.sdp, streamType })
                });

                if (!response.ok) throw new Error("Error del servidor proxy");
                const answerSdp = await response.text();
                await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSdp }));
            } catch (err) {
                console.error(err);
                setError('No se pudo establecer la transmisión.');
                setLoading(false);
            }
        };

        startWebRTC();

        const pauseTimer = setTimeout(() => {
            setIsPaused(true);
        }, 180000);

        const usageInterval = setInterval(async () => {
            try {
                const token = localStorage.getItem('token');
                const projectId = localStorage.getItem('activeProjectId');
                await axios.post(`http://localhost:4000/api/cameras/${cam.id}/usage`, {
                    durationSeconds: 15,
                    streamType: streamType
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-project-id': projectId
                    }
                });
                setSecondsWatched(prev => prev + 15);
            } catch (err) {
                console.error("Error reportando consumo de datos:", err);
            }
        }, 15000);

        return () => {
            pc.close();
            clearTimeout(pauseTimer);
            clearInterval(usageInterval);
        };
    }, [cam.id, isPaused, streamType]);

    const formatDataEstimate = () => {
        const bytesPerSecond = streamType === 'main' ? 250000 : 64000;
        const totalBytes = secondsWatched * bytesPerSecond;
        if (totalBytes < 1000000) {
            return `${(totalBytes / 1024).toFixed(1)} KB`;
        }
        return `${(totalBytes / 1024 / 1024).toFixed(1)} MB`;
    };

    if (isPaused) {
        return (
            <div className="absolute inset-0 z-20 bg-theme-panel/95 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center text-theme-text-primary">
                <AlertCircle size={48} className="text-amber-400 mb-4 animate-pulse" />
                <h4 className="text-lg font-bold mb-2">Transmisión Pausada</h4>
                <p className="text-xs text-theme-text-secondary max-w-sm mb-6 leading-relaxed">
                    Hemos detenido la transmisión en vivo automáticamente para evitar el consumo innecesario de tus datos celulares de la obra.
                </p>
                <button
                    onClick={() => {
                        setIsPaused(false);
                        setSecondsWatched(0);
                    }}
                    className="px-6 py-2.5 bg-gradient-to-tr from-blue-600 to-indigo-500 hover:from-blue-500 hover:to-indigo-400 text-white rounded-xl font-bold text-xs shadow-lg hover:scale-102 transition-all cursor-pointer"
                >
                    Reanudar Transmisión
                </button>
            </div>
        );
    }

    return (
        <div className="absolute inset-0 z-10 bg-black flex items-center justify-center">
            {loading && <div className="absolute z-20 flex flex-col items-center"><Loader2 size={36} className="animate-spin text-blue-500 mb-2" /><span className="text-xs text-blue-400 font-bold uppercase tracking-widest animate-pulse">Conectando WebRTC...</span></div>}
            
            <div className="absolute top-4 right-4 z-30 flex bg-black/60 backdrop-blur-md p-1 rounded-xl border border-theme-border gap-1">
                <button
                    onClick={() => setStreamType('sub')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-bold tracking-wider transition-all cursor-pointer ${
                        streamType === 'sub' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}
                >
                    SD (Sub)
                </button>
                <button
                    onClick={() => setStreamType('main')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-bold tracking-wider transition-all cursor-pointer ${
                        streamType === 'main' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}
                >
                    HD (Main)
                </button>
            </div>

            <div className="absolute bottom-4 left-4 z-30 bg-black/60 backdrop-blur-md px-3 py-1 rounded-lg border border-theme-border text-[10px] text-theme-text-secondary">
                Sesión: <span className="font-bold text-blue-400">{formatDataEstimate()}</span> est.
            </div>

            {error ? (
                <div className="flex flex-col items-center justify-center text-slate-600 relative z-20">
                    <AlertCircle size={36} className="mb-3 opacity-50 text-rose-500" />
                    <span className="text-xs font-bold tracking-widest uppercase opacity-75">{error}</span>
                </div>
            ) : (
                <video 
                    ref={videoRef}
                    className="w-full h-full object-contain"
                    autoPlay
                    playsInline
                    muted
                    controls
                />
            )}
            
            {(loading || error) && cam.last_image_url && (
                <img src={cam.last_image_url} alt="Preview" className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale blur-sm" />
            )}
        </div>
    );
};

export default LiveCameras;
